import { voltFetch } from "../voltApi";
import type {
  Committee,
  Draft,
  IterationResult,
  NewDraft,
  SlackTakeover,
  Version,
  WorkshopStatus,
} from "./types";

const call = <T>(path: string, init?: RequestInit) =>
  voltFetch<T>(`/workshop/api${path}`, init);

const json = (method: string, body?: unknown): RequestInit => ({
  method,
  ...(body === undefined ? {} : { body: JSON.stringify(body) }),
});

export const workshopApi = {
  status: () => call<WorkshopStatus>("/status"),
  committees: () => call<Committee[]>("/committees"),
  drafts: () => call<Draft[]>("/drafts"),
  draft: (id: string) => call<Draft>(`/drafts/${id}`),
  create: (draft: NewDraft) => call<Draft>("/drafts", json("POST", draft)),
  save: (id: string, change: { entwurf?: string; gremium?: string }) =>
    call<Draft>(`/drafts/${id}`, json("PATCH", change)),
  restart: (id: string) => call<Draft>(`/drafts/${id}/restart`, json("POST")),
  remove: (id: string) =>
    call<{ status: string }>(`/drafts/${id}`, json("DELETE")),
  iterate: (id: string, prompt: string, baseNr: number) =>
    call<IterationResult>(
      `/drafts/${id}/iterate`,
      json("POST", { prompt, basis_nr: baseNr }),
    ),
  versions: (id: string) => call<Version[]>(`/drafts/${id}/versions`),
  version: (id: string, nr: number) =>
    call<Version>(`/drafts/${id}/versions/${nr}`),
  restore: (id: string, nr: number, baseNr: number) =>
    call<{ entwurf: Draft; nr: number }>(
      `/drafts/${id}/versions/${nr}/restore`,
      json("POST", { basis_nr: baseNr }),
    ),
  slackDeadline: (id: string) =>
    call<{ datum: string | null; gremium: string | null }>(
      `/drafts/${id}/slack/deadline`,
    ),
  slackTakeover: (id: string, frist: string) =>
    call<SlackTakeover>(`/drafts/${id}/slack`, json("POST", { frist })),
};

/** File as base64 without the data: prefix; the server stores it in the bucket. */
export const readFileBase64 = (
  file: File,
): Promise<{ name: string; mime: string; data: string }> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () =>
      reject(new Error(`${file.name} konnte nicht gelesen werden`));
    reader.onload = () => {
      const s = String(reader.result ?? "");
      const comma = s.indexOf(",");
      resolve({
        name: file.name,
        mime: file.type || "application/octet-stream",
        data: comma >= 0 ? s.slice(comma + 1) : s,
      });
    };
    reader.readAsDataURL(file);
  });

export const errorText = (e: unknown) =>
  e instanceof Error ? e.message : String(e);
